const listenerRegistry = new WeakMap()
const dataRegistry = new WeakMap()

function elementsFrom(value, root = document) {
  if (value == null) return []
  if (value instanceof DomCollection) return value.toArray()
  if (typeof value === "string") {
    if (value.trim().startsWith("<")) {
      const template = document.createElement("template")
      template.innerHTML = value.trim()
      return Array.from(template.content.children)
    }
    return Array.from(root.querySelectorAll(value))
  }
  if (value instanceof Element || value === window || value === document) return [value]
  if (typeof value[Symbol.iterator] === "function") return Array.from(value).filter(Boolean)
  return []
}

function dataValue(value) {
  if (value === "true") return true
  if (value === "false") return false
  if (value === "null") return null
  if (/^-?\d+(\.\d+)?$/.test(value || "")) return Number(value)
  if (/^[\[{]/.test(value || "")) {
    try { return JSON.parse(value) } catch (_) { /* Keep the original string. */ }
  }
  return value
}

function datasetKey(name) {
  return String(name).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
}

function eventName(name) {
  return String(name).split(".")[0]
}

export class DomCollection {
  constructor(value, root = document) {
    this.elements = elementsFrom(value, root)
    this.elements.forEach((element, index) => { this[index] = element })
  }

  get length() { return this.elements.length }

  toArray() { return [...this.elements] }

  each(callback) {
    this.elements.forEach((element, index) => callback(index, element))
    return this
  }

  find(selector) {
    return new DomCollection(this.elements.flatMap((element) => Array.from(element.querySelectorAll(selector))))
  }

  first() { return new DomCollection(this.elements.slice(0, 1)) }

  last() { return new DomCollection(this.elements.slice(-1)) }

  next(selector) {
    const nextElements = this.elements.map((element) => element.nextElementSibling).filter(Boolean)
    return new DomCollection(selector ? nextElements.filter((element) => element.matches(selector)) : nextElements)
  }

  closest(selector) {
    return new DomCollection([...new Set(this.elements.map((element) => element.closest?.(selector)).filter(Boolean))])
  }

  addClass(names) {
    const classes = String(names || "").split(/\s+/).filter(Boolean)
    return this.each((_, element) => element.classList.add(...classes))
  }

  removeClass(names) {
    const classes = String(names || "").split(/\s+/).filter(Boolean)
    return this.each((_, element) => element.classList.remove(...classes))
  }

  toggleClass(name, force) {
    return this.each((_, element) => {
      if (force === undefined) element.classList.toggle(name)
      else element.classList.toggle(name, force)
    })
  }

  hasClass(name) { return Boolean(this.elements[0]?.classList.contains(name)) }

  attr(name, value) {
    if (typeof name === "object") {
      Object.entries(name).forEach(([key, entry]) => this.attr(key, entry))
      return this
    }
    if (value === undefined) return this.elements[0]?.getAttribute(name)
    return this.each((_, element) => {
      if (value == null) element.removeAttribute(name)
      else element.setAttribute(name, String(value))
    })
  }

  removeAttr(name) { return this.each((_, element) => element.removeAttribute(name)) }

  prop(name, value) {
    const property = name === "readonly" ? "readOnly" : name
    if (value === undefined) return this.elements[0]?.[property]
    return this.each((_, element) => { element[property] = value })
  }

  val(value) {
    if (value === undefined) return this.elements[0]?.value
    return this.each((_, element) => { element.value = value == null ? "" : value })
  }

  text(value) {
    if (value === undefined) return this.elements[0]?.textContent
    return this.each((_, element) => { element.textContent = value == null ? "" : value })
  }

  html(value) {
    if (value === undefined) return this.elements[0]?.innerHTML
    return this.each((_, element) => { element.innerHTML = value == null ? "" : value })
  }

  data(name, value) {
    const element = this.elements[0]
    if (!element) return name === undefined ? {} : undefined
    const cache = dataRegistry.get(element) || {}
    if (name === undefined) {
      const dataset = Object.fromEntries(Object.entries(element.dataset || {}).map(([key, entry]) => [key, dataValue(entry)]))
      return { ...dataset, ...cache }
    }
    const key = datasetKey(name)
    if (value === undefined) {
      if (Object.prototype.hasOwnProperty.call(cache, key)) return cache[key]
      return dataValue(element.dataset?.[key])
    }
    return this.each((_, target) => {
      const targetCache = dataRegistry.get(target) || {}
      targetCache[key] = value
      dataRegistry.set(target, targetCache)
    })
  }

  css(name, value) {
    if (typeof name === "object") {
      return this.each((_, element) => Object.assign(element.style, name))
    }
    if (value === undefined) return this.elements[0] ? getComputedStyle(this.elements[0])[name] : undefined
    return this.each((_, element) => { element.style[name] = value })
  }

  show() {
    return this.each((_, element) => {
      element.hidden = false
      element.style.display = element.dataset.wbdDisplay || ""
      if (getComputedStyle(element).display === "none") element.style.display = "block"
    })
  }

  hide() {
    return this.each((_, element) => {
      if (element.style.display && element.style.display !== "none") element.dataset.wbdDisplay = element.style.display
      element.style.display = "none"
    })
  }

  toggle(force) {
    return this.each((_, element) => {
      const visible = getComputedStyle(element).display !== "none"
      const shouldShow = force === undefined ? !visible : Boolean(force)
      new DomCollection(element)[shouldShow ? "show" : "hide"]()
    })
  }

  is(selector) {
    const element = this.elements[0]
    if (!element) return false
    if (selector === ":visible") return getComputedStyle(element).display !== "none" && !element.hidden
    if (selector === ":disabled") return Boolean(element.disabled)
    return element.matches(selector)
  }

  on(name, selector, callback) {
    if (typeof selector === "function") {
      callback = selector
      selector = null
    }
    const type = eventName(name)
    return this.each((_, element) => {
      const listener = selector
        ? (event) => {
            const target = event.target.closest(selector)
            if (target && element.contains(target)) callback.call(target, event)
          }
        : callback
      element.addEventListener(type, listener)
      const listeners = listenerRegistry.get(element) || []
      listeners.push({ name, type, listener })
      listenerRegistry.set(element, listeners)
    })
  }

  one(name, callback) {
    const type = eventName(name)
    return this.each((_, element) => element.addEventListener(type, callback, { once: true }))
  }

  off(name) {
    return this.each((_, element) => {
      const listeners = listenerRegistry.get(element) || []
      listeners.filter((entry) => !name || entry.name === name || entry.name.startsWith(name))
        .forEach((entry) => element.removeEventListener(entry.type, entry.listener))
      listenerRegistry.set(element, name ? listeners.filter((entry) => entry.name !== name && !entry.name.startsWith(name)) : [])
    })
  }

  trigger(name) {
    return this.each((_, element) => {
      if (["click", "focus", "blur", "select"].includes(name) && typeof element[name] === "function") element[name]()
      else element.dispatchEvent(new Event(name, { bubbles: true, cancelable: true }))
    })
  }

  empty() { return this.html("") }

  append(content) {
    const nodes = elementsFrom(content)
    return this.each((parentIndex, element) => {
      nodes.forEach((node) => element.append(parentIndex === 0 ? node : node.cloneNode(true)))
    })
  }

  clone() { return new DomCollection(this.elements.map((element) => element.cloneNode(true))) }

  insertBefore(target) {
    const destination = elementsFrom(target)[0]
    if (destination) this.elements.forEach((element) => destination.before(element))
    return this
  }

  remove() {
    return this.each((_, element) => element.remove())
  }
}

export function $(value, root = document) {
  return new DomCollection(value, root)
}
